import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import AdminLayout from '../../components/admin/AdminLayout';
import { Search, Filter, GraduationCap, CheckCircle, Clock, XCircle, Download, Loader2 } from 'lucide-react';
import { getAdminEnrollments } from '../../api/client';
import { useToast } from '../../contexts/ToastContext';

interface Enrollment {
  id: number;
  user_name: string;
  user_email: string;
  user_avatar: string | null;
  course_title: string;
  course_slug: string;
  course_price: number | null;
  status: string;
  progress_percent: number;
  payment_id: number | null;
  created_at: string;
  updated_at: string;
}

export default function AdminEnrollments(): React.JSX.Element {
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const perPage = 20;
  const toast = useToast();

  const fetchEnrollments = async (currentPage = 1) => {
    try {
      setLoading(true);
      const params: Record<string, string> = { page: String(currentPage), per_page: String(perPage) };
      if (search) params.search = search;
      if (filterStatus !== 'all') params.status = filterStatus;

      const res = await getAdminEnrollments(params);
      setEnrollments(res.data ?? []);
      setPage(res.page ?? 1);
      setTotalPages(res.totalPages ?? 1);
      setTotal(res.total ?? 0);
    } catch (err: any) {
      toast.show(err.message || 'Failed to load enrollments', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnrollments(1);
  }, [filterStatus]);

  const handleSearch = () => {
    fetchEnrollments(1);
  };

  const handleExport = () => {
    const csv = [
      ['ID', 'Student', 'Email', 'Course', 'Status', 'Progress', 'Amount', 'Enrolled At'].join(','),
      ...enrollments.map(e => [
        e.id,
        `"${e.user_name}"`,
        `"${e.user_email}"`,
        `"${e.course_title || 'N/A'}"`,
        e.status,
        `${e.progress_percent || 0}%`,
        e.course_price || 0,
        e.created_at,
      ].join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `enrollments-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-green-50 text-green-700">Completed</span>;
      case 'active':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-600">Active</span>;
      case 'cancelled':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-red-50 text-red-700">Cancelled</span>;
      case 'refunded':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-600">Refunded</span>;
      default:
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-700">Pending</span>;
    }
  };

  const getInitials = (name: string) => {
    return name?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() || '?';
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Enrollments</h1>
            <p className="text-sm text-slate-500">{total} total enrollments</p>
          </div>
          <button
            onClick={handleExport}
            disabled={enrollments.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl text-sm font-bold hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by student or course..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20"
            />
          </div>
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
            <option value="cancelled">Cancelled</option>
            <option value="refunded">Refunded</option>
          </select>
        </div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50">
                  <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-5 py-3">Student</th>
                  <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-5 py-3">Course</th>
                  <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-5 py-3">Progress</th>
                  <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-5 py-3">Status</th>
                  <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-5 py-3">Amount</th>
                  <th className="text-left text-xs font-bold text-slate-500 uppercase tracking-wider px-5 py-3">Enrolled</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-12 text-center">
                      <Loader2 className="w-6 h-6 animate-spin text-amber-500 mx-auto" />
                      <p className="text-sm text-slate-400 mt-2">Loading enrollments...</p>
                    </td>
                  </tr>
                ) : enrollments.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-12 text-center text-slate-400 text-sm">
                      No enrollments found
                    </td>
                  </tr>
                ) : (
                  enrollments.map((e) => (
                    <tr key={e.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 font-bold text-xs">
                            {getInitials(e.user_name)}
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-slate-900">{e.user_name}</p>
                            <p className="text-xs text-slate-400">{e.user_email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-600">{e.course_title || 'N/A'}</td>
                      <td className="px-5 py-4">
                        <div className="w-full max-w-[120px]">
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-slate-500">{e.progress_percent || 0}%</span>
                          </div>
                          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-amber-500 rounded-full transition-all" style={{ width: `${e.progress_percent || 0}%` }} />
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4">{getStatusBadge(e.status)}</td>
                      <td className="px-5 py-4 text-sm font-semibold text-slate-900">
                        {e.course_price ? `₹${Number(e.course_price).toLocaleString()}` : 'Free'}
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-500">
                        {e.created_at ? new Date(e.created_at).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100">
              <p className="text-xs text-slate-400">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => fetchEnrollments(page - 1)}
                  disabled={page <= 1 || loading}
                  className="px-3 py-1.5 text-xs font-medium border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  onClick={() => fetchEnrollments(page + 1)}
                  disabled={page >= totalPages || loading}
                  className="px-3 py-1.5 text-xs font-medium border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </AdminLayout>
  );
}
